import { type LearnerProject } from 'LearnerProjects';
import { type ProjectGroupMembershipSansProjectGroup } from 'ProjectGroup';
import { transformKeyCase } from 'Utils/transformKeyCase';
import {
    type GradingFields,
    type IguanaCohortInput,
    type IguanaLearnerProjectInput,
    type IguanaProjectProgressInput,
    type ProjectFieldsModalAdapterInput,
    type ProjectFieldsModalProps,
    type ProjectFieldsModalSubmissionData,
} from './ProjectFieldsModal.types';

type AdaptedProps = Omit<ProjectFieldsModalProps, 'onSaved' | 'closeModal'>;

function buildInitialFields(pp: IguanaProjectProgressInput): GradingFields {
    return {
        score: pp.score ?? null,
        provisionalScore: pp.project_submission?.provisional_score ?? pp.provisional_score ?? null,
        waived: !!pp.waived,
        resubmissionRequired: !!pp.resubmission_required,
        allowResubmission: !!pp.allow_resubmission,
        status: pp.status ?? null,
        feedback: pp.feedback ?? null,
        provisionalFeedback: pp.project_submission?.provisional_feedback ?? pp.provisional_feedback ?? null,
        idVerified: pp.id_verified ?? null,
        resubmissionDueDate: pp.resubmission_due_date ?? null,
        projectSubmissionSimilarityAndOriginalityStatus:
            pp.project_submission_similarity_and_originality_status ?? null,
        projectSubmissionNeedsAdminReview: !!pp.project_submission_needs_admin_review,
        projectSubmissionPrivateNotes: pp.project_submission_private_notes ?? null,
        projectSubmissionSelectedProjectVariant: pp.project_submission_selected_project_variant ?? null,
        projectSubmissionPresentationTranscript: pp.project_submission_presentation_transcript ?? null,
    };
}

export function buildSubmissionData(pp: IguanaProjectProgressInput): ProjectFieldsModalSubmissionData {
    return {
        projectSubmission: pp.project_submission
            ? { id: pp.project_submission.id, user_id: pp.project_submission.user_id ?? null }
            : null,
        historicalProjectSubmissions: pp.historical_project_submissions || [],
        projectGroupMembers: pp.project_group_members || [],
        projectGradedEventLastLoggedAt: pp.project_graded_event_last_logged_at ?? null,
        projectSubmissionAutogradingStatus: pp.project_submission_autograding_status ?? null,
        projectSubmissionAutogradingSkippedFormatTypes: pp.project_submission_autograding_skipped_format_types || [],
        projectSubmissionPresentationDurationMs: pp.project_submission_presentation_duration_ms ?? null,
    };
}

function buildProjectData(project: IguanaLearnerProjectInput, cohort: IguanaCohortInput): AdaptedProps['project'] {
    return {
        id: project.id,
        title: project.title,
        requirementIdentifier: project.requirement_identifier,
        passingScore: project.getPassingScore(cohort),
        isPresentationProject: !!project.isPresentationProject,
        hasPresentationLinkFormat: !!project.hasPresentationLinkFormat,
        similarityAndOriginalityCheckRequired: !!project.similarity_and_originality_check_required,
        autoGradingEnabled: !!project.auto_grading_enabled,
        feedbackTemplate: project.feedback_template ?? null,
        variants: (project.variant_config?.variants || []).map(variant => ({
            slug: variant.slug,
            label: variant.label,
            feedbackTemplate: variant.feedback_template ?? null,
        })),
        projectDocuments: (project.project_documents || []).map(document => ({
            url: document.url,
            title: document.title ?? null,
            fileFileName: document.file_file_name ?? null,
        })),
        validStatusOptions: project.validStatusOptionsForProgressRecords || [],
    };
}

// The shared adapter for both call sites. It converts the Grading Portal's Iguana models, and the
// gradebook's snake_case records of the same shape, into the React modal's plain camelCase props.
// One copy keeps the conversion identical, and we test it once.
export function buildProjectFieldsModalProps({
    project,
    projectProgress: pp,
    cohort,
    user,
    currentUser,
    projectGroupMembership,
}: ProjectFieldsModalAdapterInput): AdaptedProps {
    return {
        initialFields: buildInitialFields(pp),
        identity: {
            id: pp.id ?? null,
            userId: user.id,
            requirementIdentifier: pp.requirement_identifier ?? project.requirement_identifier,
            learnerProjectId: project.id,
            programType: cohort.program_type ?? null,
        },
        project: buildProjectData(project, cohort),
        cohort: { id: cohort.id, isExecEd: !!cohort.isExecEd, programType: cohort.program_type ?? null },
        user: { id: user.id, name: user.name, email: user.email },
        submissionData: buildSubmissionData(pp),
        isAdmin: !!(currentUser?.roles || []).some(role => role.name === 'admin' && role.resource_id === null),
        // The upload modal needs a full camelCased LearnerProject and membership. Both call sites
        // pass snake_case Iguana data, so convert it here, as the original directive did.
        uploadProject: transformKeyCase(project.asJson ? project.asJson() : project, {
            to: 'camelCase',
        }) as unknown as LearnerProject,
        projectGroupMembership: projectGroupMembership
            ? (transformKeyCase(projectGroupMembership, {
                  to: 'camelCase',
              }) as unknown as ProjectGroupMembershipSansProjectGroup)
            : null,
    };
}

// Merges the project_progress returned by the admin upload API into the modal's live state. A newly
// uploaded submission then appears without a reopen. The original directive did the same: it kept the
// modal open and patched its Iguana record in place.
//
// The endpoint serializes with `as_json(for: :cohort_gradebook)`, so the response carries every field,
// including the variant the upload form collected and the group members. Read them from the response.
// An earlier version kept both from the pre-upload state, which showed a variant the new submission
// did not have, with hasChanges false.
export function applyUploadedProjectProgress(uploaded: IguanaProjectProgressInput): {
    initialFields: GradingFields;
    submissionData: ProjectFieldsModalSubmissionData;
} {
    return {
        initialFields: buildInitialFields(uploaded),
        submissionData: buildSubmissionData(uploaded),
    };
}
